import { z } from "zod"

/*
 * Клиенты продавцов (встреча 17.10.2026): привязка покупателя к магазину,
 * который его привёл по своей ссылке или QR, и льготный срок без комиссии
 * площадки на заказы у этого магазина - удерживается только эквайринг.
 *
 * Не путать с партнёрской программой (promo-partners.ts): там партнёр
 * приводит покупателя площадке и получает вознаграждение.
 */

export const SellerClientSettingsSchema = z.object({
  enabled: z.boolean(),
  /** Льготный срок в месяцах от даты привязки. */
  graceMonths: z.number().int().min(0).max(36),
  /** Ставка эквайринга, удерживаемая вместо комиссии: доля, 0.03 = 3 %. */
  acquiringRate: z.number().min(0).max(0.2),
})
export type SellerClientSettings = z.infer<typeof SellerClientSettingsSchema>

/** Правка настроек; `reason` - только для журнала действий консоли. */
export const SellerClientSettingsPatchSchema = SellerClientSettingsSchema.partial().extend({
  reason: z.string().trim().max(500).optional(),
})
export type SellerClientSettingsPatch = z.infer<typeof SellerClientSettingsPatchSchema>

/** Персональный срок магазина; null - вернуть общий. */
export const SellerClientShopTermsInputSchema = z.object({
  graceMonths: z.number().int().min(0).max(36).nullable(),
  note: z.string().trim().max(500).optional(),
  reason: z.string().trim().max(500).optional(),
})
export type SellerClientShopTermsInput = z.infer<typeof SellerClientShopTermsInputSchema>

/** Привязать покупателя к магазину вручную или перепривязать (текущая привязка снимается). */
export const SellerClientAdminBindSchema = z.object({
  buyerUserId: z.string().trim().min(1).max(120),
  shopId: z.string().trim().min(1).max(120),
  reason: z.string().trim().min(3, "Укажите причину").max(500),
})
export type SellerClientAdminBind = z.infer<typeof SellerClientAdminBindSchema>

export const SellerClientUnbindSchema = z.object({
  reason: z.string().trim().min(3, "Укажите причину").max(500),
})
export type SellerClientUnbind = z.infer<typeof SellerClientUnbindSchema>

export const ShopInviteBindSchema = z.object({
  code: z.string().trim().min(1).max(32),
  visitor: z.string().trim().max(120).optional(),
})

/*
 * Ссылка или QR (PDF «Доработки платформы», п.2-3; 27.10.2026). QR из
 * генератора кабинета несёт utm_source=qr_generator - такой переход
 * засчитывается как «qr», остальные - как «link».
 */
export const ReferralChannelSchema = z.enum(["link", "qr"])
export type ReferralChannel = z.infer<typeof ReferralChannelSchema>

export const ReferralPlatformSchema = z.enum(["web", "ios", "android"])
export type ReferralPlatform = z.infer<typeof ReferralPlatformSchema>

const utmValue = z.string().trim().max(200).optional()
export const ReferralUtmSchema = z.object({
  source: utmValue,
  medium: utmValue,
  campaign: utmValue,
  content: utmValue,
  term: utmValue,
})
export type ReferralUtm = z.infer<typeof ReferralUtmSchema>

/** Метки ссылки QR из генератора кабинета: web.osmix.ru/s/<код>?utm_source=qr_generator&… */
export const QR_UTM_SOURCE = "qr_generator"
export const QR_UTM_MEDIUM = "social"

export const ShopInviteVisitSchema = z.object({
  code: z.string().trim().min(1).max(32),
  visitor: z.string().trim().min(1).max(120),
  channel: ReferralChannelSchema.optional(),
  utm: ReferralUtmSchema.optional(),
  platform: ReferralPlatformSchema.optional(),
})

/**
 * Приложение (и витрина) сообщают шлюзу, по какой ссылке открыли: короткая
 * ссылка магазина /s/<код> и/или ?promo=<код> с метками. Можно прислать
 * адрес целиком (`url`) - шлюз разберёт его сам; явные поля важнее.
 * `visitor` - clientId устройства: за ним переход закрепляется на 30 дней,
 * и при входе в аккаунт (POST /buyer/device/attach) шлюз сам привязывает
 * покупателя к магазину и засчитывает партнёрский код.
 */
export const ReferralLandingSchema = z.object({
  visitor: z.string().trim().min(1).max(120),
  url: z.string().trim().max(2000).optional(),
  code: z.string().trim().max(64).optional(),
  promo: z.string().trim().max(64).optional(),
  channel: ReferralChannelSchema.optional(),
  utm: ReferralUtmSchema.optional(),
  platform: ReferralPlatformSchema.optional(),
})
export type ReferralLandingInput = z.infer<typeof ReferralLandingSchema>

/** Что закреплено за устройством (30 дней от перехода): магазин ссылки и промокод для корзины. */
export type ReferralPin = {
  /** Магазин ссылки /s/<код> - первое касание в окне 30 дней. */
  shop: (ShopInvite & { channel: ReferralChannel; expiresAt: string }) | null
  /** Действующий покупательский промокод из ссылки - подставить в корзину (последний переход). */
  promo: { code: string; channel: ReferralChannel; expiresAt: string } | null
}

export type ReferralLandingResult = ReferralPin & {
  /** Вошедшему покупателю - результат привязки к магазину сразу. */
  binding?: ShopInviteBindResult
  /** Вошедшему покупателю - засчитан ли он партнёру по промокоду. */
  attributed?: boolean
}

/** Ссылка против QR: переходы и что они дали магазину за период. */
export type SellerLinkSourceRow = {
  channel: ReferralChannel
  /** Переходы: одно устройство в день. */
  visits: number
  /** Новые клиенты, закреплённые за магазином в периоде. */
  clients: number
  /** Клиенты на 100 переходов, доля 0..1. */
  conversion: number
  /** Заказы клиентов этого канала у магазина в периоде. */
  orders: number
  revenue: number
}

export type SellerLinkSources = {
  from: string
  to: string
  link: SellerLinkSourceRow
  qr: SellerLinkSourceRow
}

export type ShopInvite = {
  code: string
  shopId: string
  /** Адрес витрины магазина: /shop/<slug>. */
  slug: string
  shopName: string
}

/** Результат попытки привязки; `reason` - почему не привязан. */
export type ShopInviteBindResult = {
  bound: boolean
  reason:
    | "bound"
    | "already_this_shop"
    | "already_bound"
    | "not_new"
    | "own_shop"
    | "invalid_code"
    | "window_expired"
  shopName?: string
}

export type SellerClientBindingStatus = "active" | "ended"

export type SellerClientBinding = {
  id: string
  buyerUserId: string
  buyerName: string
  buyerPhone: string
  buyerEmail: string
  shopId: string
  shopName: string
  source: "link" | "admin"
  code: string | null
  visitedAt: string | null
  boundAt: string
  graceMonths: number
  graceEndsAt: string
  inGrace: boolean
  status: SellerClientBindingStatus
  reason: string | null
  createdByName: string | null
  endedAt: string | null
  endedByName: string | null
  endReason: string | null
  /** Заказы у магазина привязки (за всё время), из них в льготный срок. */
  orders: number
  graceOrders: number
  revenue: number
  savedCommission: number
}

export type SellerClientShopSummary = {
  shopId: string
  shopName: string
  code: string | null
  /** Персональный срок; null - общий. */
  graceMonths: number | null
  graceNote: string
  visits: number
  clients: number
  inGrace: number
  orders: number
  graceOrders: number
  revenue: number
  /** Комиссия, которую площадка не взяла (обычная ставка минус эквайринг). */
  savedCommission: number
  /** Удержано эквайрингом по льготным заказам. */
  acquiringRetained: number
}

export type SellerClientsAdminSummary = {
  from: string
  to: string
  settings: SellerClientSettings
  totals: Omit<SellerClientShopSummary, "shopId" | "shopName" | "code" | "graceMonths" | "graceNote">
  shops: SellerClientShopSummary[]
}

/** Клиент в кабинете продавца: контакты маской, как в статистике промокодов. */
export type SellerClientRow = {
  id: string
  name: string
  phone: string
  email: string
  boundAt: string
  graceEndsAt: string
  inGrace: boolean
  orders: number
  revenue: number
  savedCommission: number
}

export type SellerClientOrderRow = {
  orderId: string
  number: number | null
  createdAt: string
  clientName: string
  goods: number
  /** Удержанная ставка и обычная (у льготного заказа - разные). */
  rate: number
  regularRate: number
  savedCommission: number
  grace: boolean
}

export type SellerClientsOverview = {
  from: string
  to: string
  program: {
    enabled: boolean
    graceMonths: number
    acquiringRate: number
  }
  /**
   * `url` - короткая ссылка магазина; `qrUrl` - она же с метками QR
   * (utm_source=qr_generator); `qrSvg` - QR этой ссылки, коррекция H.
   */
  invite: { code: string; url: string; qrUrl: string; qrSvg: string; shopName: string }
  totals: {
    visits: number
    clients: number
    inGrace: number
    orders: number
    revenue: number
    savedCommission: number
  }
  clients: SellerClientRow[]
  orders: SellerClientOrderRow[]
  /** Ссылка против QR за тот же период. */
  bySource: SellerLinkSources
}
